import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'hotel/navigation';

import { ButtonLink } from 'components/Button';
import Loading from 'components/Loading';
import { RequestFilesSection } from 'components/Requests/RequestFilesSection';
import { SectionHeader } from 'components/SectionHeader';
import {
  RosterRequestStatus,
  RosterRequestType,
  useCheckForDuplicateChangeRequestsQuery,
  useRosterRequestQuery,
} from 'generated/graphql';
import { popToast } from 'utilities/popToast';

import AssignmentsList from './AssignmentsList';
import Details from './Details';
import Header from './Header';
import RemarksList from './RemarksList';
import RequestData from './RequestData';
import RequestType from './RequestType';

const RequestDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Use the id in your component logic
  const { data, loading, refetch } = useRosterRequestQuery({
    variables: {
      id: id ?? '',
    },
    onError: (error) => {
      popToast(error.message, { type: 'error' });
      navigate('/requests');
    },
  });

  const request = data?.rosterRequest;

  // Determine if files can be edited (only for editable request statuses)
  const canEditFiles =
    request?.status === RosterRequestStatus.RequestReceived ||
    request?.status === RosterRequestStatus.MoreInfoRequired;

  const rosterRoomAssignmentIds =
    data?.rosterRequest.rosterRoomAssignments?.map((a) => a.id) ?? [];

  const { data: duplicateRequestsData, error: duplicateRequestsError } =
    useCheckForDuplicateChangeRequestsQuery({
      variables: {
        ids: rosterRoomAssignmentIds,
      },
      skip:
        !rosterRoomAssignmentIds ||
        rosterRoomAssignmentIds.length < 1 ||
        request?.client.id == null,
    });
  const duplicateRequests =
    duplicateRequestsData?.duplicateRosterChangeRequests?.filter(
      (r) => r.id !== id,
    ) ?? [];

  if (loading) {
    return <Loading />;
  }

  if (!request) {
    return <div>Request not found</div>;
  }

  // Check if this is a group reservation request that needs full width
  const isGroupReservation =
    request.requestType === RosterRequestType.NewGroupReservation;

  return (
    <>
      <Helmet>
        <title>Request Details</title>
      </Helmet>

      <div className="mb-40 flex items-start justify-center p-4 md:p-6 xl:px-0">
        <div className="mx-auto max-w-7xl flex-1">
          <SectionHeader className="font-medium" headline="Request Details">
            <ButtonLink to="/requests" text="Return to Requests" />
          </SectionHeader>

          {isGroupReservation ? (
            // Full width layout for group reservations
            // Traveler view is handled internally by NewGroupReservationRequest
            <div className="space-y-8">
              {/* Header and Type grouped together */}
              <div className="space-y-4">
                {/* Header */}
                <div className="text-base text-primaryGray">
                  <Header request={request} />
                </div>

                {/* Type */}
                <h2 className="text-2xl font-medium text-black">
                  <RequestType request={request} />
                </h2>
              </div>

              {/* Request Data takes full width */}
              {request?.requestData && <RequestData request={request} />}

              {/* Roster Assignments */}
              {request.rosterRoomAssignments &&
                request.rosterRoomAssignments?.length > 0 && (
                  <div>
                    <AssignmentsList
                      request={request}
                      onRemoveAssignment={() => {
                        // removeAssignment({
                        //   variables: {
                        //     rosterRequestId: request.id,
                        //     rosterRoomAssignmentId: assignmentId,
                        //   },
                        // });
                      }}
                      duplicateRequests={duplicateRequests}
                      duplicateRequestsError={duplicateRequestsError}
                    />
                  </div>
                )}
            </div>
          ) : (
            // Two-column layout for other request types
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="col-span-2">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="text-base text-primaryGray">
                    <Header request={request} />
                  </div>

                  {/* Type */}
                  <h2 className="text-2xl font-medium text-black">
                    <RequestType request={request} />
                  </h2>

                  {/* Details */}
                  <Details request={request} />

                  {/* Attachments */}
                  <RequestFilesSection
                    rosterRequestId={request.id}
                    files={request.files}
                    onFilesChanged={() => refetch()}
                    readOnly={!canEditFiles}
                  />

                  {/* Remarks */}
                  <div>
                    <h2 className="mb-5 text-lg text-primaryGray">Remarks</h2>
                    <RemarksList remarks={request.remarks} />
                  </div>

                  {/* Request Data */}
                  {request?.requestData && (
                    <div>
                      <RequestData request={request} />
                    </div>
                  )}

                  {/* Roster Assignments */}
                  {request.rosterRoomAssignments &&
                    request.rosterRoomAssignments?.length > 0 && (
                      <div>
                        <AssignmentsList
                          request={request}
                          onRemoveAssignment={() => {
                            // removeAssignment({
                            //   variables: {
                            //     rosterRequestId: request.id,
                            //     rosterRoomAssignmentId: assignmentId,
                            //   },
                            // });
                          }}
                          duplicateRequests={duplicateRequests}
                          duplicateRequestsError={duplicateRequestsError}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RequestDetails;
